import type { SidebarNavSpaceFolder } from "@design";
import type { SidebarSectionResponse } from "@/api";

// The API calls them "sections"; the sidebar calls them "folders". General is the server's home for every space
// not in a folder; the sidebar doesn't draw it, and shows those spaces as "All spaces" instead.

/** Server sections -> the design system's folders. */
export function toNavFolders(sections: SidebarSectionResponse[] | undefined): SidebarNavSpaceFolder[] {
  return (sections ?? []).map((s) => ({
    key: s.uuid,
    name: s.name,
    kind: s.kind,
    sortMode: s.sortMode,
    collapsed: s.collapsed,
    spaceKeys: s.spaceUuids ?? [],
  }));
}

/** Drops spaces the user can no longer see (e.g. just left or deleted) before the server catches up. */
export function visibleOnly(folders: SidebarNavSpaceFolder[], spaceKeys: string[]): SidebarNavSpaceFolder[] {
  const visible = new Set(spaceKeys);
  return folders.map((f) => ({ ...f, spaceKeys: f.spaceKeys.filter((k) => visible.has(k)) }));
}

// --- optimistic updates (the server's answer replaces them) -------------------------------------

type Sections = SidebarSectionResponse[];

export function generalKey(sections: Sections): string | undefined {
  return sections.find((s) => s.kind === "general")?.uuid;
}

export function moveSpaceLocally(
  sections: Sections,
  spaceKey: string,
  folderKey: string,
  index: number | null,
): Sections {
  return sections.map((s) => {
    const others = (s.spaceUuids ?? []).filter((k) => k !== spaceKey);
    if (s.uuid !== folderKey) return { ...s, spaceUuids: others };
    const at = index === null ? others.length : Math.min(index, others.length);
    return { ...s, spaceUuids: [...others.slice(0, at), spaceKey, ...others.slice(at)] };
  });
}

export function reorderFoldersLocally(sections: Sections, folderKeys: string[]): Sections {
  const general = sections.filter((s) => s.kind === "general");
  const custom = sections.filter((s) => s.kind === "custom");
  const rank = new Map(folderKeys.map((k, i) => [k, i]));
  const ordered = [...custom].sort((a, b) => (rank.get(a.uuid) ?? custom.length) - (rank.get(b.uuid) ?? custom.length));
  return [...general, ...ordered];
}

export function updateFolderLocally(
  sections: Sections,
  key: string,
  change: Partial<SidebarSectionResponse>,
): Sections {
  return sections.map((s) => (s.uuid === key ? { ...s, ...change } : s));
}

export function deleteFolderLocally(sections: Sections, key: string): Sections {
  const gone = sections.find((s) => s.uuid === key)?.spaceUuids ?? [];
  return sections
    .filter((s) => s.uuid !== key)
    .map((s) => (s.kind === "general" ? { ...s, spaceUuids: [...(s.spaceUuids ?? []), ...gone] } : s));
}

/** Pinned spaces in the order the user dragged them to. */
export function reorderPinsLocally<T extends { uuid: string }>(pins: T[], order: string[]): T[] {
  const rank = new Map(order.map((k, i) => [k, i]));
  return [...pins].sort((a, b) => (rank.get(a.uuid) ?? pins.length) - (rank.get(b.uuid) ?? pins.length));
}
